import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Loader2, X, Check, Search } from "lucide-react";

// Shared (dispatch IncidentDetail + MDT): correct an address or call type
// entered in error. Every change is audited with a mandatory reason.
export const CallCorrection = ({ incident, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [address, setAddress] = useState("");
  const [apt, setApt] = useState("");
  const [reason, setReason] = useState("");
  const [ctSearch, setCtSearch] = useState("");
  const [ctResults, setCtResults] = useState([]);
  const [ctPick, setCtPick] = useState(null); // {code,label,discipline,priority}
  const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-[11px] outline-none focus:border-[#F59E0B] font-mono text-white w-full rounded-sm";

  const searchTypes = async (q) => {
    setCtSearch(q); setCtPick(null);
    if (q.trim().length < 2) { setCtResults([]); return; }
    try {
      const r = await client.get(`/call-types?search=${encodeURIComponent(q.trim())}&limit=8`);
      setCtResults(r.data || []);
    } catch { /* ignore */ }
  };

  const submit = async () => {
    if (!reason.trim()) { toast.error("A correction reason is required"); return; }
    if (!address.trim() && !ctPick) { toast.error("Enter a corrected address and/or pick a new call type"); return; }
    setBusy(true);
    try {
      await client.patch(`/incidents/${incident.id}/correct`, {
        address: address.trim() || null,
        apt: address.trim() ? (apt.trim() || "") : null,
        call_type_code: ctPick?.code || null,
        reason: reason.trim(),
      });
      toast.success("Call corrected — change logged to the event log");
      setOpen(false); setAddress(""); setApt(""); setReason(""); setCtSearch(""); setCtResults([]); setCtPick(null);
      onChanged?.();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Correction failed");
    } finally { setBusy(false); }
  };

  if (incident.status === "closed") return null;

  if (!open) return (
    <button data-testid="correct-call-btn" onClick={() => setOpen(true)}
      className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-head font-bold uppercase bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/25 transition-colors rounded-sm">
      <Pencil size={11} /> Correct Address / Call Type
    </button>
  );

  return (
    <div data-testid="correct-call-form" className="border border-[#F59E0B]/50 bg-[#141518] p-3 rounded-sm space-y-2">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase text-[#F59E0B]">
        <Pencil size={12} /> Correct Call — audited change
        <button data-testid="correct-cancel" onClick={() => setOpen(false)} className="ml-auto text-[#9CA3AF] hover:text-white"><X size={13} /></button>
      </div>
      <div className="font-mono text-[10px] text-[#6B7280]">
        Current: {incident.call_type_label} ({incident.call_type_code}) @ {incident.location?.address}{incident.location?.apt ? ` · Unit ${incident.location.apt}` : ""}
      </div>
      <div className="grid grid-cols-[1fr_90px] gap-1.5">
        <input data-testid="correct-address" value={address} onChange={(e) => setAddress(e.target.value)}
          placeholder="Corrected address (leave blank to keep current)" className={inp} />
        <input data-testid="correct-apt" value={apt} onChange={(e) => setApt(e.target.value)} placeholder="Apt/Unit" className={inp} />
      </div>
      <div className="relative">
        <div className="flex items-center gap-1.5">
          <Search size={12} className="text-[#9CA3AF] shrink-0" />
          <input data-testid="correct-cts" value={ctPick ? `${ctPick.label} (${ctPick.code})` : ctSearch}
            onChange={(e) => searchTypes(e.target.value)}
            placeholder="Search new call type (leave blank to keep current)" className={inp} />
          {ctPick && <button data-testid="correct-ct-clear" onClick={() => { setCtPick(null); setCtSearch(""); }} className="text-[#9CA3AF] hover:text-white"><X size={12} /></button>}
        </div>
        {!ctPick && ctResults.length > 0 && (
          <div className="absolute z-20 left-5 right-0 mt-1 border border-[#2C2D35] bg-[#0d0e11] rounded-sm max-h-44 overflow-y-auto">
            {ctResults.map((ct) => (
              <button key={ct.code} data-testid={`correct-ct-${ct.code}`} onClick={() => { setCtPick(ct); setCtResults([]); }}
                className="w-full text-left px-2.5 py-1.5 text-[11px] font-mono text-[#C9CDD4] hover:bg-[#1a1b20] flex items-center gap-2">
                <span className="text-[9px] font-bold uppercase px-1 py-px rounded-sm border"
                  style={{ color: ct.discipline === "police" ? "#60A5FA" : ct.discipline === "fire" ? "#f87171" : "#4ade80", borderColor: "#2C2D35" }}>{ct.discipline}</span>
                {ct.label} <span className="text-[#6B7280]">({ct.code}) · P{ct.priority}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <input data-testid="correct-reason" value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Reason for correction * (required — written to event log)" className={inp} />
      <button data-testid="correct-submit" onClick={submit} disabled={busy}
        className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-head font-bold uppercase bg-[#F59E0B] text-black rounded-sm disabled:opacity-50">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Apply Correction
      </button>
    </div>
  );
};
